import { ReactElement, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, Alert, Row, Col, Button } from "antd";
import { CheckCircleOutlined, InstagramOutlined, MailOutlined } from "@ant-design/icons";
import PhoneInput from "react-phone-input-2";
import { v4 as uuidv4 } from "uuid";
import * as EmailValidator from "email-validator";
import "../../assets/css/phone-input.css";
import firebase from "firebase/app";
import { CREATE_BOOKING_REQUEST, LOGIN_USER_FAILURE, LOGIN_USER_SUCCESS, CHNAGE_LANGUAGE } from "../../redux/constants";
import { ContentRow, FormCol, FormInput, FormButton, NavigateBar, NavigateButton } from "../../components/GlobalStyles";
import { getAppState, getBookingState } from "../../redux/selectors";
import Logo from "../../assets/images/setpoint-logo.png";


export function LoginWithPhone(): ReactElement {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const [phoneNumber, setPhoneNumber] = useState<string>();
  const [fullName, setFullName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [showError, setShowError] = useState<boolean>(false);
  const [showFullName, setShowFullName] = useState<boolean>(false);
  const [gender, setGender] = useState<string>("male");
  const [loggedInUser, setLoggedInUser] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const { isShareBooking, shareBookingTime, shareBookingDuration, shareBookingCourt, preBranch, preBookingDate } = useSelector(getBookingState);
  const { locale } = useSelector(getAppState);

  const renderRecaptcha = (): any => {
    const recaptchaVerifier = new firebase.auth.RecaptchaVerifier(
      "recaptcha_container",
      {
        size: "invisible",
        callback: (response: any) => {
          console.log(response);
        },
      }
    );
    return recaptchaVerifier;
  };

  const goEmailLogin = () => {
    history.push("/login");
  };

  const bookIfNeeded = (userId: string) => {
    // Create new booking with Share data
    const newBooking = {
      bookingId: uuidv4(),
      userId,
      branchId: preBranch?.firestoreRef,
      courtId: shareBookingCourt,
      date: preBookingDate,
      period: shareBookingDuration,
      start: shareBookingTime,
      end: "",
      ball: false,
      club: preBranch?.clubData.firestoreRef,
      note: "",
      price: preBranch?.clubData.price[shareBookingDuration],
      currency: preBranch?.clubData.currency,
    };
    dispatch({
      type: CREATE_BOOKING_REQUEST,
      payload: { data: newBooking },
    })
  };

  const loginFn = () => {
    const appVerifier = renderRecaptcha();
    if (phoneNumber) {
      setErrorMsg("");
      setShowError(false);
      setLoading(true);
      firebase
        .auth()
        .signInWithPhoneNumber(`+${phoneNumber}`, appVerifier)
        .then((confirmationResult) => {
          const code = window.prompt("Enter OTP code");
          const otpCode = `${code}`;
          confirmationResult
            .confirm(otpCode)
            .then((result) => {
              // User signed in successfully.
              const { user } = result;
              firebase
                .firestore()
                .collection("users")
                .doc(user?.uid)
                .get()
                .then((doc) => {
                  if (doc.exists) {
                    // Login Success
                    const userData = doc.data();
                    dispatch({
                      type: LOGIN_USER_SUCCESS,
                      payload: {
                        user: userData,
                        emailVerified: user?.emailVerified,
                        authType: "phone",
                      },
                    });
                    if (isShareBooking) bookIfNeeded(doc.id);
                    setTimeout(() => {
                      setLoading(false);
                      if (userData?.role === "regular") {
                        history.push("/booking");
                      } else {
                        history.push("/dashboard");
                      }
                    }, 2000);
                  } else {
                    // Not exist user data;
                    setShowFullName(true);
                    setLoggedInUser(user);
                    setLoading(false);
                  }
                });
            })
            .catch((e) => {
              dispatch({
                type: LOGIN_USER_FAILURE,
                payload: { error: true, errorMsg: e.message },
              });
              setErrorMsg(e.message);
              setShowError(true);
              setLoading(false);
            });
        })
        .catch((err) => {
          dispatch({
            type: LOGIN_USER_FAILURE,
            payload: { error: true, errorMsg: err.message },
          });
          setErrorMsg(err.message);
          setShowError(true);
          setLoading(false);
        });
    }
  };
  const register = () => {
    const mailformat = /^w+([.-]?w+)*@w+([.-]?w+)*(.w{2,3})+$/;
    if (fullName === "" || email === "") {
      setShowError(true);
      setErrorMsg("All fields are required!");
    } else if (!EmailValidator.validate(email)) {
      setShowError(true);
      setErrorMsg("Please enter valid email address!");
    } else {
      setShowError(false);
      setLoading(true);
      const newUser = {
        id: loggedInUser?.uid,
        fullName,
        email,
        gender,
        photoUrl: "",
        instagram: "",
        facebook: "",
        linkedIn: "",
        twitter: "",
        club: "",
        friends: "",
        emailVerified: false,
        phoneNumber: `+${phoneNumber}`,
        phoneNumberVerified: true,
        creationTime: new Date().toISOString(),
        lastSignInTime: new Date().toISOString(),
        role: "regular",
      };
      firebase.firestore().collection("users").doc(newUser.id).set(newUser)
        .then(() => {
          dispatch({
            type: LOGIN_USER_SUCCESS,
            payload: {
              user: newUser,
              emailVerified: false,
              authType: "phone",
            },
          });
          if (isShareBooking) bookIfNeeded(newUser.id);
          setTimeout(() => {
            setLoading(false);
            window.location.replace("/booking");
          }, 2000);
        })
        .catch((e) => {
          console.log(e);
          setLoading(false);
        });
    }
  };

  const onChangeGender = (value: string) => {
    setGender(value);
  };

  const onChangeLanguage = (code: string) => {
    dispatch({
      type: CHNAGE_LANGUAGE,
      payload: code
    })
    i18n.changeLanguage(code);
  }


  return (
    <ContentRow className="auth-container">
      <div className="landing-language-select-container">
        <Button className="language-selector" onClick={() => onChangeLanguage(locale === "en" ? "ar" : "en")}>{t("common.language")}</Button>
      </div>
      <Card className="auth-form">
        <FormCol span={24}>
          <img src={Logo} alt="Logo" className="auth-logo" />
          <h4 className="setpoint-slogan">{t("auth.find_your_match")}</h4>
          <PhoneInput
            country="ae"
            value={phoneNumber}
            onlyCountries={["eg", "bh", "qa", "sa", "kw", "ae"]}
            onChange={(phone) => setPhoneNumber(phone)}
            containerStyle={{ marginTop: 10 }}
            inputStyle={{ color: "#fff" }}
            buttonStyle={{ backgroundColor: "transparent", border: "none" }}
          />
          {showFullName && (
            <>
              <FormInput
                placeholder={t("auth.fullName")}
                type="text"
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
                onPressEnter={register}
              />
              <FormInput
                placeholder={t("auth.email")}
                type="text"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                onPressEnter={register}
              />
              <Row style={{ width: "100%", marginTop: 10 }}>
                <Col span={12} style={{ paddingRight: 5 }}>
                  <Button
                    block
                    type={gender === "male" ? "primary" : "default"}
                    style={{ height: 40, borderRadius: 5 }}
                    icon={gender === "male" ? <CheckCircleOutlined /> : null}
                    onClick={() => onChangeGender("male")}
                  >
                    Male
                  </Button>
                </Col>
                <Col span={12} style={{ paddingLeft: 5 }}>
                  <Button
                    block
                    type={gender === "female" ? "primary" : "default"}
                    style={{ height: 40, borderRadius: 5 }}
                    icon={gender === "female" ? <CheckCircleOutlined /> : null}
                    onClick={() => onChangeGender("female")}
                  >
                    Female
                  </Button>
                </Col>
              </Row>
            </>
          )}
          <div id="recaptcha_container" />
          {!showFullName && (
            <FormButton
              type="primary"
              block
              onClick={loginFn}
              loading={loading}
            >
              {t("auth.register_with_phone")}
            </FormButton>
          )}
          {showFullName && (
            <FormButton
              type="primary"
              block
              onClick={register}
              loading={loading}
            >
              {t("auth.continue")}
            </FormButton>
          )}
          {showError && <Alert message={errorMsg} type="error" style={{ width: "100%", marginTop: 10 }} />}
          <NavigateBar>
            <span style={{ color: "#fff" }}>{t("auth.use_email")}</span>
            <NavigateButton type="link" onClick={goEmailLogin}>
              {t("auth.login")}
            </NavigateButton>
          </NavigateBar>
        </FormCol>
      </Card>
      <div className="contact-us-container">
        {/* <span>{t("menu.contact_us")}: </span>
        <a href="mailto:hi@setpointapp.com" target="_blank" rel="noreferrer">hi@setpointapp.com</a> */}
        <Button href="mailto:hi@setpointapp.com" type="link" icon={<MailOutlined />} target="_blank">hi@setpointapp.com</Button>
        <Button href="https://www.instagram.com/setpointapp/" type="link" icon={<InstagramOutlined />} target="_blank">Instagram</Button>
      </div>
    </ContentRow>
  );
}
